<script setup lang="ts">
import Input from '@/components/ui/input/Input.vue'
import Dropdown from '@/components/ui/dropdown/Dropdown.vue'
import Button from '@/components/ui/button/Button.vue'
import { computed, inject, watch } from 'vue'
import { AlertSettingsViewModel } from '../viewmodels/AlertSettings.viewmodel'
import HelpTooltip from '@/@web/ui/components/HelpTooltip.vue'

const vm = inject<AlertSettingsViewModel>('alert-settings-view-model') as AlertSettingsViewModel

const title = computed(() =>
	vm.alertSettings.value.id
		? 'Editar Configurações de Alerta'
		: 'Cadastrar Configurações de Alerta',
)

watch(vm.openForm, (value) => {
	if (value) vm.getMonitoredAreas()
})
</script>

<template>
	<section>
		<h1 class="text-H5">{{ title }}</h1>
		<div class="flex flex-col gap-4 mt-8">
			<Input
				v-model="vm.alertSettings.value.description"
				label="descrição*"
			>
				<template #tooltip>
					<help-tooltip
						title="Descrição"
						description="Defina a descrição que irá identificar a configuração de alerta que está sendo cadastrada."
					/>
				</template>
			</Input>

			<Dropdown
				v-model="vm.alertSettings.value.monitoredAreas"
				label="fazenda/setor*"
				:options="vm.monitoredAreaOptions.value"
			>
				<template #tooltip>
					<help-tooltip
						title="Fazenda/Setor"
						description="Selecione para qual fazenda(s)/setor(es) essa configuração será aplicada."
					/>
				</template>
			</Dropdown>

			<Input
				label="buffer (km)*"
				type="number"
				:message="vm.bufferFieldMessage.value"
				:error="vm.isBufferInvalid.value"
				v-model.number="vm.alertSettings.value.buffer"
			>
				<template #tooltip>
					<help-tooltip
						title="Buffer"
						description="Defina qual é o tamanho do buffer para fora do polígono da fazenda onde deseja receber os alertas."
					/>
				</template>
			</Input>

			<div>
				<label class="text-label flex gap-1 items-center"
					>Confiança da detecção (%)

					<help-tooltip
						title="Confiança da detecção"
						description="<p>Define o percentual mínimo de confiança da detecção para receber notificações de alerta. Ex: ao definir 70%, você só receberá notificações com confiança igual ou superior.</p>"
					/>
				</label>

				<v-slider
					v-model="vm.alertSettings.value.minDetectionConfidence"
					color="#34617E"
					track-color="#34617E"
					max="100"
					min="0"
					hide-details
					class="flex items-center"
				>
					<template #append>
						<span class="text-s-bold !text-blue-tints">{{
							vm.alertSettings.value.minDetectionConfidence
						}}</span>
					</template>
				</v-slider>
			</div>
		</div>

		<div class="flex gap-4 mt-8">
			<Button
				text="voltar"
				secondary
				:disabled="vm.fetching.value"
				class="w-1/2 justify-center"
				@click="() => (vm.openForm.value = false)"
			/>
			<Button
				v-if="vm.alertSettings.value.id"
				text="salvar"
				:loading="vm.fetching.value"
				:disabled="!vm.isFormValid"
				class="w-1/2 justify-center"
				@click="() => vm.updateAlertSettings()"
			/>
			<Button
				v-else
				text="cadastrar"
				:loading="vm.fetching.value"
				:disabled="!vm.isFormValid"
				class="w-1/2 justify-center"
				@click="() => vm.createAlertSettings()"
			/>
		</div>
	</section>
</template>

<style scoped>
section {
	padding: 50px 32px 0 50px;
}

:deep(.v-input) {
	align-items: center;
}
</style>
